import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Clock, MapPin, CheckCircle2, Zap, Snowflake, Camera, Cpu } from "lucide-react";
import { AREAS } from "../data/areas";
import { SERVICES } from "../data/services";
import { buildWaUrl, WA_MESSAGES } from "../data/site";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";
import { BoltIcon, ElectricBackground } from "../components/Decorations";

const ICONS = {
  "instalasi-perbaikan-listrik": Zap,
  "service-ac": Snowflake,
  "instalasi-cctv": Camera,
  "panel-industri": Cpu,
};

export default function AreaLanding() {
  const { area: slug } = useParams();
  const area = AREAS.find((a) => a.slug === slug);
  if (!area) return <Navigate to="/" replace />;

  const otherAreas = AREAS.filter((a) => a.slug !== slug && a.region === area.region).slice(0, 6);

  const title = `Jasa Listrik ${area.name} — Teknisi Profesional di ${area.name}`;
  const description = `Butuh jasa listrik di ${area.name}? ${area.description} Respon ${area.response}. Chat WhatsApp ListrikBatam.com sekarang.`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `ListrikBatam.com — ${area.name}`,
    areaServed: area.name,
    description,
    telephone: "+62 811-7006-5656",
  };

  return (
    <div data-testid={`area-landing-${slug}`}>
      <Seo
        title={title}
        description={description}
        canonical={`https://listrikbatam.com/jasa-listrik/${slug}`}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative min-h-[68vh] flex items-end text-white overflow-hidden">
        <ElectricBackground />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12 pt-28 pb-16 w-full">
          <Reveal>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-[#FFB341]"
              data-testid="area-back-home"
            >
              <ArrowLeft className="w-4 h-4" /> Beranda
            </Link>
            <div className="mt-6 inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 text-white px-3 py-1.5 rounded-full text-xs font-semibold">
              <MapPin className="w-3.5 h-3.5 text-[#FFB341]" />
              {area.region}
            </div>
            <h1 className="mt-5 font-display text-5xl md:text-7xl lg:text-[92px] font-black leading-[0.95] tracking-tighter text-balance">
              Jasa Listrik
              <br />
              <span className="inline-flex items-center gap-3">
                <span className="text-[#FF6B00]">{area.name}</span>
                <BoltIcon className="w-10 h-14 sm:w-12 sm:h-16 bolt-flicker" color="#FF6B00" />
              </span>
            </h1>
            <p className="mt-6 max-w-2xl text-white/80 text-base md:text-lg">
              {area.description}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={buildWaUrl(`Halo, saya butuh jasa listrik/teknisi di ${area.name}, Batam.`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="area-wa-cta"
                className="inline-flex items-center gap-3 bg-[#FF6B00] text-white font-bold rounded-full pl-7 pr-5 py-4 text-base shadow-[0_12px_30px_rgba(255,107,0,0.45)] hover:-translate-y-1 transition-all"
              >
                Chat Teknisi {area.name}
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white text-[#FF6B00]">
                  <ArrowUpRight className="w-4 h-4" />
                </span>
              </a>
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white px-4 py-3 rounded-full text-sm backdrop-blur-md">
                <Clock className="w-4 h-4 text-[#FFB341]" />
                Respon {area.response}
              </div>
            </div>
            <div className="mt-4 text-xs text-white/60 flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FF6B00] animate-pulse" />
              Layanan 24 jam · Teknisi terdekat · Panggilan ke lokasi
            </div>
          </Reveal>
        </div>
      </section>

      {/* Highlights */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <Reveal className="lg:col-span-5">
          <div className="text-[#FF6B00] font-semibold tracking-[0.2em] uppercase text-xs mb-3">
            / Karakter {area.name}
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-black text-[#0F4C75] leading-[1.05] tracking-tight text-balance">
            Kami paham karakter <span className="text-[#FF6B00]">{area.name}.</span>
          </h2>
          <p className="mt-5 text-[#1B262C]/75 md:text-lg">
            Setiap wilayah punya kebutuhan listrik yang berbeda. Tim kami sudah
            terbiasa menangani proyek di {area.name} dengan tantangan uniknya.
          </p>
        </Reveal>

        <Reveal className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-4" stagger>
          {area.highlights.map((h) => (
            <div
              key={h}
              className="rounded-2xl bg-white border border-[#E5E7EB] p-6 hover:-translate-y-1 hover:border-[#FF6B00] transition-all"
            >
              <CheckCircle2 className="w-6 h-6 text-[#FF6B00] mb-3" />
              <div className="font-display font-bold text-[#0F4C75] text-base md:text-lg">
                {h}
              </div>
            </div>
          ))}
        </Reveal>
      </section>

      {/* Services for this area */}
      <section className="bg-[#F8FAFC] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <Reveal className="mb-10 max-w-2xl">
            <div className="text-[#FF6B00] font-semibold tracking-[0.2em] uppercase text-xs mb-3">
              / Layanan di {area.name}
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-black text-[#0F4C75] leading-[1.05] tracking-tight">
              Listrik, AC, CCTV & lainnya untuk {area.name}.
            </h2>
            <p className="mt-4 text-[#1B262C]/75 md:text-lg">
              Layanan teknis kelistrikan dan peralatan rumah tangga dalam satu
              solusi terpadu — tersedia di {area.name} dan sekitarnya.
            </p>
          </Reveal>
          <Reveal className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" stagger>
            {SERVICES.slice(0, 4).map((svc) => {
              const Icon = ICONS[svc.slug] || Zap;
              return (
                <Link
                  key={svc.slug}
                  to={`/layanan#${svc.slug}`}
                  data-testid={`area-service-${svc.slug}`}
                  className="group rounded-3xl bg-white border border-[#E5E7EB] p-6 hover:-translate-y-1 hover:border-[#3282B8] transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#F8FAFC] text-[#0F4C75] group-hover:bg-[#FF6B00] group-hover:text-white flex items-center justify-center transition-colors mb-5">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="font-display font-bold text-lg text-[#0F4C75] mb-2">
                    {svc.title}
                  </div>
                  <p className="text-xs text-[#1B262C]/70 line-clamp-3">
                    {svc.short}
                  </p>
                </Link>
              );
            })}
          </Reveal>
        </div>
      </section>

      {/* SEO paragraph */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12 py-20 md:py-24">
        <Reveal>
          <div className="text-[#FF6B00] font-semibold tracking-[0.2em] uppercase text-xs mb-3">
            / Jasa Listrik {area.name}
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-black text-[#0F4C75] leading-tight mb-6">
            Teknisi listrik terpercaya untuk {area.name}.
          </h2>
          <div className="space-y-4 text-[#1B262C]/80 md:text-lg leading-relaxed">
            <p>
              Sebagai penyedia <strong>jasa listrik {area.name}</strong>, ListrikBatam.com
              siap menangani segala kebutuhan kelistrikan di wilayah {area.name}, mulai
              dari instalasi baru, perbaikan darurat, hingga maintenance berkala. Kami
              juga melayani <strong>service AC</strong>, <strong>instalasi CCTV</strong>,
              <strong> service kulkas</strong>, <strong>service mesin cuci</strong>, dan
              <strong> instalasi water heater</strong> — semua dalam satu tim teknis
              terpadu. Tim kami hadir cepat dengan estimasi respon {area.response}.
            </p>
            <p>
              Teknisi kami bersertifikat kompetensi ketenagalistrikan dan bekerja sesuai
              standar PUIL 2011. Kami juga melayani wilayah sekitar {area.name} dan
              seluruh {area.region}—termasuk pemasangan panel industri, grounding, arrester
              petir, hingga solar panel rumah tangga.
            </p>
            <p>
              Hubungi kami via WhatsApp untuk mendapatkan estimasi biaya gratis dan
              konsultasi awal. Kami akan menjelaskan scope pekerjaan, material yang
              digunakan, serta garansi—sebelum Anda setuju dan pekerjaan dimulai.
              Tersedia layanan 24 jam untuk kebutuhan darurat di {area.name}.
            </p>
          </div>
        </Reveal>
      </section>

      {/* Related areas */}
      {otherAreas.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 pb-20">
          <Reveal>
            <div className="text-[#FF6B00] font-semibold tracking-[0.2em] uppercase text-xs mb-3">
              / Area lain di {area.region}
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-[#0F4C75] mb-6">
              Kami juga melayani
            </h3>
          </Reveal>
          <Reveal className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" stagger>
            {otherAreas.map((o) => (
              <Link
                key={o.slug}
                to={`/jasa-listrik/${o.slug}`}
                data-testid={`related-area-${o.slug}`}
                className="rounded-2xl bg-white border border-[#E5E7EB] px-4 py-4 text-center hover:-translate-y-1 hover:border-[#FF6B00] transition-all"
              >
                <span className="block font-display font-bold text-[#0F4C75] text-sm md:text-base">
                  {o.name}
                </span>
                <span className="block text-[10px] uppercase tracking-wider text-[#1B262C]/50 mt-1">
                  {o.region}
                </span>
              </Link>
            ))}
          </Reveal>
        </section>
      )}

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 pb-20">
        <Reveal className="rounded-3xl bg-gradient-to-br from-[#0F4C75] to-[#1B262C] text-white p-10 md:p-14 relative overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-25" />
          <BoltIcon className="absolute top-6 right-8 w-14 h-20 float-slow opacity-80" color="#FF6B00" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <h3 className="font-display text-3xl md:text-5xl font-black leading-[1.02] tracking-tight">
                Teknisi siap ke <span className="text-[#FF6B00]">{area.name}.</span>
              </h3>
              <p className="mt-4 text-white/80 max-w-xl">
                Chat WhatsApp sekarang — kami konfirmasi jadwal dan estimasi biaya hari ini juga.
              </p>
            </div>
            <div className="lg:col-span-4 lg:justify-self-end">
              <a
                href={buildWaUrl(`Halo, butuh jasa listrik di ${area.name}.`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="area-final-wa-cta"
                className="inline-flex items-center gap-3 bg-[#FF6B00] text-white font-bold rounded-full pl-7 pr-5 py-4 text-base shadow-[0_12px_30px_rgba(255,107,0,0.4)] hover:-translate-y-1 transition-all"
              >
                Chat {area.name}
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white text-[#FF6B00]">
                  <ArrowUpRight className="w-4 h-4" />
                </span>
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
